/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useStore } from '@hooks/useStore';
import { strFromBase64 } from '@utils/convertString';
import { AutocompleteSelect } from '@inputs/AutocompleteSelect';
import { ILocationAutocopmleteProps, ILocationData } from './types';

export function LocationAutocomplete({
  afterSelect,
  afterMultiSelect,
  name,
  useOwnDataAsValue = false,
  ...props
}: ILocationAutocopmleteProps) {
  const {
    heremapsStore: { getLocationSuggestions },
  } = useStore();
  const { setValue } = useFormContext();

  const [options, setOptions] = useState<ILocationData[]>([]);

  const { mutate, isPending } = useMutation({
    mutationFn: getLocationSuggestions,
    mutationKey: ['getLocationSuggestions'],
    onSuccess: (res) => {
      setOptions(
        res?.reduce((acc: any[], item: any) => {
          if (item.address && item.resultType !== 'place')
            acc.push({ name: item.address.label, placeId: item.id, position: item.position });
          return acc;
        }, [] as ILocationData[]) || []
      );
    },
  });

  const onSelectHandler = useCallback(
    (val: ILocationData) => {
      if (val) {
        const { placeId, name: dataName, ...rest } = val;
        setValue(
          useOwnDataAsValue ? name : `${name}Data`,
          { placeId, name },
          { shouldValidate: true }
        );
        afterSelect && afterSelect({ placeId, name: dataName }, rest);
      } else {
        setValue(name, null, { shouldValidate: true });
        afterSelect && afterSelect(null);
      }
    },
    [useOwnDataAsValue]
  );

  const onMultiSelectHandler = useCallback(
    (vals: ILocationData[]) => {
      if (vals && vals.length) {
        const newVals = vals.map(({ placeId, name: dataName }) => ({ placeId, name: dataName }));
        setValue(useOwnDataAsValue ? name : `${name}Data`, newVals, { shouldValidate: true });
        afterMultiSelect && afterMultiSelect(newVals, vals);
      } else {
        setValue(name, null, { shouldValidate: true });
        afterMultiSelect && afterMultiSelect(null);
      }
    },
    [useOwnDataAsValue]
  );

  const onSearch = useCallback(async (search: string) => {
    const q = strFromBase64(search);
    if (q) {
      mutate({ q });
    }
    return undefined;
  }, []);

  return (
    <AutocompleteSelect
      {...props}
      name={useOwnDataAsValue ? name : `${name}Data`}
      data={options}
      // data={
      //   placePredictions
      //     ? placePredictions?.map(({ place_id, description }) => ({ id: place_id, name: description }))
      //     : defaultValues.map(({ placeId, name }) => ({ id: placeId, name }))
      // }
      onSelect={onSelectHandler}
      onMultiSelect={onMultiSelectHandler}
      onSearch={onSearch}
      disableFiltering
      optionLabel="name"
      optionValue="placeId"
      base64Converting
      isLoading={isPending}
    />
  );
}
